import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
import ListAltIcon from "@mui/icons-material/ListAlt";
import OIExportSection from "@/components/oi-export/OIExportSection";
import { JOB_KIND_OPTIONS, defaultStrikeBand, intervalForKind, jobDataLabel } from "@/components/oi-backfill/jobKind";
import OptionMinutePreview from "@/components/oi-backfill/OptionMinutePreview";
import OIIntervalCleanupSection from "@/components/oi-backfill/OIIntervalCleanupSection";
import DataIntegrityAuditSection from "@/components/oi-backfill/DataIntegrityAuditSection";
import oiBackfillService from "@/services/oiBackfillService";
import type {
  BackfillJobProgressResponse,
  BackfillJobRead,
  BackfillJobKind,
  BackfillJobStatus,
  BackfillWorkItemRead,
  BackfillWorkItemStatus,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";

// The system currently only ever seeds two Instrument rows — NIFTY and
// BANKNIFTY (see backend migration 0002 / `tests/conftest.py::seed_instruments`,
// verified against the legacy `oi_mon.INDEX_CONFIG` dict). There is no
// frontend endpoint yet that lists instruments generically, so this is a
// hardcoded fixed pair rather than a fetched list — a stated assumption,
// not a guess: if a third instrument is ever added to the system, this
// list needs a matching update (or a real `GET /instruments` endpoint).
const SYMBOL_OPTIONS = ["NIFTY", "BANKNIFTY"] as const;

// Breeze's `get_historical_data_v2` accepts these interval strings; the
// backend does not itself enumerate/validate them (see
// `app.integrations.market_data.breeze_historical_provider`), so this list
// is this page's own reasonable default set, not a server-verified enum.
// Each job kind has exactly one interval (`runner.check_interval`):
// - OI_CHAIN_5MIN writes the OI raw tables, which record no interval, so only
//   "5minute" is allowed there (other intervals would mix with the 5-minute
//   data — see the clean-up section below).
// - INDEX_CANDLES_1MIN writes `market_candle_history` (index OHLC only, no
//   option chain), one Breeze request per instrument per trading day.
// - OPTION_PRICES_1MIN writes `option_minute_candle` (1-minute option OHLC for
//   backtests), one Breeze request per planned contract per trading day.
// See components/oi-backfill/jobKind.ts for the kind → interval rules.

const JOB_STATUS_FILTER_OPTIONS: Array<{ label: string; value: BackfillJobStatus | "" }> = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Running", value: "RUNNING" },
  { label: "Paused", value: "PAUSED" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Failed", value: "FAILED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const WORK_ITEM_STATUS_FILTER_OPTIONS: Array<{ label: string; value: BackfillWorkItemStatus | "" }> = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Running", value: "RUNNING" },
  { label: "Success", value: "SUCCESS" },
  { label: "Failed", value: "FAILED" },
  { label: "Skipped", value: "SKIPPED" },
];

type ChipColor = "default" | "info" | "warning" | "success" | "error";

const JOB_STATUS_COLOR: Record<BackfillJobStatus, ChipColor> = {
  PENDING: "default",
  RUNNING: "info",
  PAUSED: "warning",
  COMPLETED: "success",
  FAILED: "error",
  CANCELLED: "default",
};

const WORK_ITEM_STATUS_COLOR: Record<BackfillWorkItemStatus, ChipColor> = {
  PENDING: "default",
  RUNNING: "info",
  SUCCESS: "success",
  FAILED: "error",
  SKIPPED: "default",
};

const NON_TERMINAL_JOB_STATUSES: BackfillJobStatus[] = ["PENDING", "RUNNING"];

function JobStatusChip({ status }: { status: BackfillJobStatus }) {
  return <Chip label={status} size="small" color={JOB_STATUS_COLOR[status]} />;
}

function WorkItemStatusChip({ status }: { status: BackfillWorkItemStatus }) {
  return <Chip label={status} size="small" color={WORK_ITEM_STATUS_COLOR[status]} variant="outlined" />;
}

function formatDateTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

function isoDateDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function OIBackfillPage() {
  // ---- Job list ----
  const [jobs, setJobs] = useState<BackfillJobRead[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<BackfillJobStatus | "">("");

  const loadJobs = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setJobsLoading(true);
      try {
        const result = await oiBackfillService.listJobs({
          status: statusFilter || undefined,
          limit: 100,
        });
        setJobs(result);
        setJobsError(null);
      } catch (err) {
        setJobsError(extractErrorMessage(err));
      } finally {
        if (!opts.silent) setJobsLoading(false);
      }
    },
    [statusFilter]
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Silently refresh the job list every 8s while at least one listed job is
  // still PENDING/RUNNING, so operators don't have to manually refresh to
  // see a job finish.
  useEffect(() => {
    const hasActiveJob = jobs.some((j) => NON_TERMINAL_JOB_STATUSES.includes(j.status));
    if (!hasActiveJob) return;
    const timer = globalThis.setInterval(() => loadJobs({ silent: true }), 8000);
    return () => globalThis.clearInterval(timer);
  }, [jobs, loadJobs]);

  // ---- Create job form ----
  const [symbolCodes, setSymbolCodes] = useState<string[]>(["NIFTY", "BANKNIFTY"]);
  const [fromDate, setFromDate] = useState(isoDateDaysAgo(365));
  const [toDate, setToDate] = useState(isoToday());
  const [jobKind, setJobKind] = useState<BackfillJobKind>("OI_CHAIN_5MIN");
  const interval = intervalForKind(jobKind);
  const isIndexKind = jobKind === "INDEX_CANDLES_1MIN";
  const isOptionMinuteKind = jobKind === "OPTION_PRICES_1MIN";
  const [strikeBand, setStrikeBand] = useState(10);
  const [maxConcurrency, setMaxConcurrency] = useState(3);
  const [minRequestIntervalMs, setMinRequestIntervalMs] = useState(300);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createNotice, setCreateNotice] = useState<string | null>(null);

  const toggleSymbol = (symbol: string) => {
    setSymbolCodes((prev) =>
      prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]
    );
  };

  const handleCreateJob = async () => {
    setCreating(true);
    setCreateError(null);
    setCreateNotice(null);
    try {
      const job = await oiBackfillService.createJob({
        job_kind: jobKind,
        symbol_codes: symbolCodes,
        from_date: fromDate,
        to_date: toDate,
        interval,
        ...(isIndexKind ? {} : { strike_band: strikeBand }),
        max_concurrency: maxConcurrency,
        min_request_interval_ms: minRequestIntervalMs,
      });
      setCreateNotice(`Job ${job.id} created and running in the background.`);
      await loadJobs();
      handleOpenDetail(job.id);
    } catch (err) {
      setCreateError(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  // ---- Row actions (pause/resume/cancel) ----
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const applyUpdatedJob = (updated: BackfillJobRead) => {
    setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
    setDetailProgress((prev) => (prev && prev.job.id === updated.id ? { ...prev, job: updated } : prev));
  };

  const runJobAction = async (jobId: string, action: "pause" | "resume" | "cancel") => {
    setActionLoadingId(jobId);
    setActionError(null);
    try {
      const fn =
        action === "pause"
          ? oiBackfillService.pauseJob
          : action === "resume"
          ? oiBackfillService.resumeJob
          : oiBackfillService.cancelJob;
      const updated = await fn(jobId);
      applyUpdatedJob(updated);
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setActionLoadingId(null);
    }
  };

  // ---- Job detail / progress dialog ----
  const [detailJobId, setDetailJobId] = useState<string | null>(null);
  const [detailProgress, setDetailProgress] = useState<BackfillJobProgressResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const handleOpenDetail = (jobId: string) => {
    setDetailJobId(jobId);
    setDetailProgress(null);
    setDetailError(null);
  };

  const handleCloseDetail = () => {
    setDetailJobId(null);
    setDetailProgress(null);
  };

  // Fetch immediately when the detail dialog opens (or target job changes).
  useEffect(() => {
    if (!detailJobId) return;
    let cancelled = false;
    setDetailLoading(true);
    oiBackfillService
      .getJobProgress(detailJobId)
      .then((result) => {
        if (!cancelled) setDetailProgress(result);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailJobId]);

  // Poll every 5s only while the open job is still PENDING/RUNNING.
  useEffect(() => {
    if (!detailJobId) return;
    if (!detailProgress || !NON_TERMINAL_JOB_STATUSES.includes(detailProgress.job.status)) return;
    const timer = globalThis.setInterval(async () => {
      try {
        const result = await oiBackfillService.getJobProgress(detailJobId);
        setDetailProgress(result);
        setJobs((prev) => prev.map((j) => (j.id === result.job.id ? result.job : j)));
      } catch (err) {
        setDetailError(extractErrorMessage(err));
      }
    }, 5000);
    return () => globalThis.clearInterval(timer);
  }, [detailJobId, detailProgress]);

  // ---- Work item log dialog ----
  const [workItemsJobId, setWorkItemsJobId] = useState<string | null>(null);
  const [workItems, setWorkItems] = useState<BackfillWorkItemRead[]>([]);
  const [workItemsLoading, setWorkItemsLoading] = useState(false);
  const [workItemsError, setWorkItemsError] = useState<string | null>(null);
  const [workItemStatusFilter, setWorkItemStatusFilter] = useState<BackfillWorkItemStatus | "">("");
  const [wiPage, setWiPage] = useState(0);
  const [wiPageSize, setWiPageSize] = useState(25);
  const prevWorkItemsJobId = useRef<string | null>(null);

  const handleOpenWorkItems = (jobId: string) => {
    setWorkItemsJobId(jobId);
    setWorkItemStatusFilter("");
    setWiPage(0);
  };

  const handleCloseWorkItems = () => {
    setWorkItemsJobId(null);
    setWorkItems([]);
  };

  const loadWorkItems = useCallback(async () => {
    if (!workItemsJobId) return;
    setWorkItemsLoading(true);
    setWorkItemsError(null);
    try {
      const result = await oiBackfillService.listWorkItems(workItemsJobId, {
        status: workItemStatusFilter || undefined,
      });
      setWorkItems(result);
    } catch (err) {
      setWorkItemsError(extractErrorMessage(err));
    } finally {
      setWorkItemsLoading(false);
    }
  }, [workItemsJobId, workItemStatusFilter]);

  useEffect(() => {
    if (!workItemsJobId) return;
    if (prevWorkItemsJobId.current !== workItemsJobId) {
      setWiPage(0);
      prevWorkItemsJobId.current = workItemsJobId;
    }
    loadWorkItems();
  }, [workItemsJobId, loadWorkItems]);

  const pagedWorkItems = workItems.slice(wiPage * wiPageSize, wiPage * wiPageSize + wiPageSize);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        OI Historical Backfill
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Start, monitor, and control historical option-chain backfill jobs (ATM ± strike band, CE/PE,
        current weekly / next weekly / current monthly expiries). Jobs run in the background and are
        resumable — pausing keeps all progress; resuming continues from the database's own record of
        pending/failed work items.
      </Typography>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Start New Backfill Job
          </Typography>

          {createError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          {createNotice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateNotice(null)}>
              {createNotice}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Data"
                value={jobKind}
                onChange={(e) => {
                  const kind = e.target.value as BackfillJobKind;
                  setJobKind(kind);
                  setStrikeBand(defaultStrikeBand(kind));
                }}
                fullWidth
                inputProps={{ "data-testid": "backfill-job-kind" }}
              >
                {JOB_KIND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {isIndexKind
                  ? "Index OHLC candles only (no option chain), one Breeze request per instrument per trading day. Used by 1-minute strategies such as DEMA-WMA V4."
                  : isOptionMinuteKind
                    ? "1-minute option prices for backtests (the DEMA-WMA 'Option 1-minute' P&L model): the day's index range ± the strike band, CE and PE, nearest expiries; one Breeze request per contract per day. Stored separately from the OI tables; contracts already stored are skipped."
                    : "Index spot plus option chain OI at 5-minute bars (unchanged behaviour)."}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" gutterBottom>
                Symbols
              </Typography>
              <FormGroup row>
                {SYMBOL_OPTIONS.map((symbol) => (
                  <FormControlLabel
                    key={symbol}
                    control={
                      <Checkbox
                        checked={symbolCodes.includes(symbol)}
                        onChange={() => toggleSymbol(symbol)}
                      />
                    }
                    label={symbol}
                  />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                label="From date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="To date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                select
                label="Interval"
                value={interval}
                fullWidth
                disabled
                helperText="Set by the Data choice"
              >
                <MenuItem value={interval}>{interval}</MenuItem>
              </TextField>
            </Grid>

            {!isIndexKind && (
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Strike band (± strikes)"
                  type="number"
                  value={strikeBand}
                  onChange={(e) => setStrikeBand(Math.max(1, Math.min(isOptionMinuteKind ? 20 : 50, Number(e.target.value))))}
                  inputProps={{ min: 1, max: isOptionMinuteKind ? 20 : 50 }}
                  helperText={isOptionMinuteKind ? "Around the day's index low / high" : undefined}
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={6} sm={2}>
              <TextField
                label="Max concurrency"
                type="number"
                value={maxConcurrency}
                onChange={(e) => setMaxConcurrency(Math.max(1, Math.min(20, Number(e.target.value))))}
                inputProps={{ min: 1, max: 20 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Min request interval (ms)"
                type="number"
                value={minRequestIntervalMs}
                onChange={(e) => setMinRequestIntervalMs(Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Grid>

            {isOptionMinuteKind && (
              <Grid item xs={12}>
                <OptionMinutePreview symbolCodes={symbolCodes} fromDate={fromDate} toDate={toDate} strikeBand={strikeBand} />
              </Grid>
            )}

            <Grid item xs={12}>
              {creating && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Planning work items — this can take a minute or two for a wide date range across
                  both symbols (the backend resolves one live spot price per contract expiry before
                  the job appears below). Please don't close this page; the button will re-enable
                  once the job is created.
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={handleCreateJob}
                disabled={creating || symbolCodes.length === 0 || !fromDate || !toDate}
                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {creating ? "Planning job…" : "Start Backfill Job"}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Tip: a full 12-month, both-symbols job can take 1-2 minutes just to plan before it
                shows up in the table below — this is expected, not a hang. Narrower ranges/fewer
                symbols create almost instantly.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Backfill Jobs</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as BackfillJobStatus | "")}
              sx={{ minWidth: 160 }}
            >
              {JOB_STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title="Refresh">
              <IconButton onClick={() => loadJobs()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {jobsError && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }} onClose={() => setJobsError(null)}>
            {jobsError}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Symbols</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Date Range</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Progress</TableCell>
                <TableCell>Concurrency</TableCell>
                <TableCell>Started</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>{job.symbol_codes.join(", ")}</TableCell>
                  <TableCell>
                    {(() => {
                      const d = jobDataLabel(job);
                      return d.legacy ? (
                        <Tooltip title="Legacy 1-minute job against the 5-minute OI tables (the source of the mixed-interval rows).">
                          <Chip size="small" color="warning" label={`${d.label} (legacy)`} />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2">{d.label}</Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {job.from_date} → {job.to_date}
                  </TableCell>
                  <TableCell>
                    <JobStatusChip status={job.status} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 160 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, job.progress_pct)}
                        sx={{ mb: 0.5 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {job.completed_work_items}/{job.total_work_items} ({job.progress_pct.toFixed(1)}%)
                        {job.failed_work_items > 0 ? ` · ${job.failed_work_items} failed` : ""}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{job.max_concurrency}</TableCell>
                  <TableCell>{formatDateTime(job.started_at)}</TableCell>
                  <TableCell align="right">
                    {actionLoadingId === job.id ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : (
                      <>
                        <Tooltip title="View details">
                          <IconButton onClick={() => handleOpenDetail(job.id)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View work item log">
                          <IconButton onClick={() => handleOpenWorkItems(job.id)}>
                            <ListAltIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Pause">
                          <span>
                            <IconButton
                              disabled={job.status !== "RUNNING"}
                              onClick={() => runJobAction(job.id, "pause")}
                            >
                              <PauseIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Resume">
                          <span>
                            <IconButton
                              disabled={job.status !== "PAUSED"}
                              onClick={() => runJobAction(job.id, "resume")}
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <span>
                            <IconButton
                              color="error"
                              disabled={job.status === "COMPLETED" || job.status === "CANCELLED"}
                              onClick={() => runJobAction(job.id, "cancel")}
                            >
                              <CancelIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!jobsLoading && jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No backfill jobs yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Independent enhancement — does not read or write anything above;
          see OIExportSection's own module and app.services.oi_export
          docstrings for why this deliberately never touches the backfill
          job/work-item state above it. */}
      <OIIntervalCleanupSection
        onUseWindow={(symbol, from, to) => {
          setSymbolCodes([symbol]);
          setFromDate(from);
          setToDate(to);
          setJobKind("OI_CHAIN_5MIN");
          setCreateNotice(`Backfill form set to ${symbol} ${from} → ${to} (5minute). Review and press Start.`);
          globalThis.scrollTo?.({ top: 0, behavior: "smooth" });
        }}
      />
      <DataIntegrityAuditSection />
      <OIExportSection />

      {/* Job detail / progress dialog */}
      <Dialog open={Boolean(detailJobId)} onClose={handleCloseDetail} maxWidth="sm" fullWidth>
        <DialogTitle>Backfill Job Details</DialogTitle>
        <DialogContent>
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          {detailLoading && !detailProgress && <CircularProgress size={24} />}
          {detailProgress && (
            <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <JobStatusChip status={detailProgress.job.status} />
                <Typography variant="body2" color="text.secondary">
                  {detailProgress.job.id}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={Math.min(100, detailProgress.job.progress_pct)}
              />
              <Typography variant="body2">
                {detailProgress.job.completed_work_items}/{detailProgress.job.total_work_items} work
                items complete ({detailProgress.job.progress_pct.toFixed(1)}%) —{" "}
                {detailProgress.job.failed_work_items} failed
              </Typography>

              <Divider />

              <Typography variant="body2">
                <strong>Symbols:</strong> {detailProgress.job.symbol_codes.join(", ")}
              </Typography>
              <Typography variant="body2">
                <strong>Date range:</strong> {detailProgress.job.from_date} →{" "}
                {detailProgress.job.to_date} ({detailProgress.job.interval})
              </Typography>
              <Typography variant="body2">
                <strong>Data:</strong> {jobDataLabel(detailProgress.job).label}
              </Typography>
              <Typography variant="body2">
                <strong>Strike band:</strong> ±{detailProgress.job.strike_band} ·{" "}
                <strong>Expiry scope:</strong> {detailProgress.job.expiry_scope}
              </Typography>
              <Typography variant="body2">
                <strong>Concurrency:</strong> {detailProgress.job.max_concurrency} ·{" "}
                <strong>Min request interval:</strong> {detailProgress.job.min_request_interval_ms}ms
              </Typography>
              <Typography variant="body2">
                <strong>Started:</strong> {formatDateTime(detailProgress.job.started_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Paused:</strong> {formatDateTime(detailProgress.job.paused_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Completed:</strong> {formatDateTime(detailProgress.job.completed_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Last heartbeat:</strong> {formatDateTime(detailProgress.job.last_heartbeat_at)}
              </Typography>
              {detailProgress.job.error_summary && (
                <Alert severity="warning">{detailProgress.job.error_summary}</Alert>
              )}

              <Divider />

              <Typography variant="subtitle2">Work items by status</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(detailProgress.work_items_by_status).map(([status, count]) => (
                  <Chip
                    key={status}
                    label={`${status}: ${count}`}
                    size="small"
                    color={WORK_ITEM_STATUS_COLOR[status as BackfillWorkItemStatus] ?? "default"}
                    variant="outlined"
                  />
                ))}
              </Stack>

              <Typography variant="subtitle2">Work items by kind</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(detailProgress.work_items_by_kind).map(([kind, count]) => (
                  <Chip key={kind} label={`${kind}: ${count}`} size="small" />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detailProgress && (
            <>
              <Button
                onClick={() => runJobAction(detailProgress.job.id, "pause")}
                disabled={detailProgress.job.status !== "RUNNING"}
              >
                Pause
              </Button>
              <Button
                onClick={() => runJobAction(detailProgress.job.id, "resume")}
                disabled={detailProgress.job.status !== "PAUSED"}
              >
                Resume
              </Button>
              <Button
                color="error"
                onClick={() => runJobAction(detailProgress.job.id, "cancel")}
                disabled={detailProgress.job.status === "COMPLETED" || detailProgress.job.status === "CANCELLED"}
              >
                Cancel
              </Button>
              <Button onClick={() => handleOpenWorkItems(detailProgress.job.id)}>Work Item Log</Button>
            </>
          )}
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Work item log dialog */}
      <Dialog open={Boolean(workItemsJobId)} onClose={handleCloseWorkItems} maxWidth="lg" fullWidth>
        <DialogTitle>Work Item Log</DialogTitle>
        <DialogContent>
          {workItemsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {workItemsError}
            </Alert>
          )}
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Select
              size="small"
              value={workItemStatusFilter}
              onChange={(e) => {
                setWorkItemStatusFilter(e.target.value as BackfillWorkItemStatus | "");
                setWiPage(0);
              }}
              sx={{ minWidth: 160 }}
            >
              {WORK_ITEM_STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {workItemsLoading && <CircularProgress size={20} />}
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kind</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Strike</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date Range</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Attempts</TableCell>
                  <TableCell>Rows (fetched/inserted)</TableCell>
                  <TableCell>Last Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedWorkItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.data_kind}</TableCell>
                    <TableCell>{item.expiry_date ?? "—"}</TableCell>
                    <TableCell>{item.strike ?? "—"}</TableCell>
                    <TableCell>{item.option_type ?? "—"}</TableCell>
                    <TableCell>
                      {item.from_date} → {item.to_date}
                    </TableCell>
                    <TableCell>
                      <WorkItemStatusChip status={item.status} />
                    </TableCell>
                    <TableCell>{item.attempts}</TableCell>
                    <TableCell>
                      {item.rows_fetched}/{item.rows_inserted}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="caption" color="error.main" noWrap title={item.last_error ?? ""}>
                        {item.last_error ?? "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {!workItemsLoading && workItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No work items found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={workItems.length}
              page={wiPage}
              onPageChange={(_e, newPage) => setWiPage(newPage)}
              rowsPerPage={wiPageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onRowsPerPageChange={(e) => {
                setWiPageSize(parseInt(e.target.value, 10));
                setWiPage(0);
              }}
            />
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWorkItems}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OIBackfillPage;
